import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import * as authService from '@/services/authService';
import { AuthTokens } from '@/types/auth';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  setTokens: (tokens: AuthTokens) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      /**
       * ログイン処理
       */
      login: async (username: string, password: string) => {
        try {
          const tokens = await authService.login({ username, password });
          
          set({
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            isAuthenticated: true,
          });
        } catch (error) {
          throw error;
        }
      },

      /**
       * ログアウト処理
       */
      logout: async () => {
        const { accessToken } = get();
        
        try {
          if (accessToken) {
            await authService.logout(accessToken);
          }
        } catch (error) {
          console.error('Logout API failed:', error);
          // APIエラーでもクライアント側のトークンは削除
        } finally {
          set({
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
          });
        }
      },

      /**
       * アクセストークンリフレッシュ
       */
      refreshAccessToken: async () => {
        const { refreshToken } = get();
        
        if (!refreshToken) {
          throw new Error('リフレッシュトークンがありません');
        }

        try {
          const tokens = await authService.refreshToken(refreshToken);
          
          set({
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            isAuthenticated: true,
          });
        } catch (error) {
          // リフレッシュ失敗時はログアウト
          set({
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      /**
       * トークンを直接設定（内部用）
       */
      setTokens: (tokens: AuthTokens) => {
        set({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          isAuthenticated: true,
        });
      },
    }),
    {
      name: 'auth-storage', // localStorage key
      storage: createJSONStorage(() => {
        // SSR-safe storage: only use localStorage on client side
        if (typeof window === 'undefined') {
          return {
            getItem: () => null,
            setItem: () => {},
            removeItem: () => {},
          }
        }
        return localStorage
      }),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      skipHydration: true, // SSR対応：クライアント側でのみhydrate
    }
  )
);